'use client';

import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useCreateMediaAttachmentMutation } from '@/hooks/useCreateMediaAttachmentMutation';
import { useFetch } from '@/hooks/useFetch';
import { useMediaUpload } from '@/hooks/useMediaUpload';
import type { UploadFileInChunksOptions } from '@/hooks/useMediaUpload/index';
import { firstMediaUuid } from '@/hooks/useMediaUpload/utils';
import type {
  ImportPrimaryClientsRequest,
  ImportPrimaryClientsResponse,
  ImportRelatedClientsRequest,
  ImportRelatedClientsResponse,
  ImportLoyaltyProgramsRequest,
  ImportLoyaltyProgramsResponse,
} from '@/types/clientDetailsApiTypes';
import type { CreateMediaAttachmentRequest } from '@/types/mediaAttachmentsApiTypes';

type UploadOptions = Omit<UploadFileInChunksOptions, 'file'> & {
  directMaxBytes?: number;
};

const IMPORTED_CLIENTS_PRIMARY_PASSENGER_UPLOAD_DEFAULTS = {
  uploadContext: 'IMPORT' as const,
  visibility: 'PUBLIC' as const,
} satisfies Omit<UploadFileInChunksOptions, 'file'>;

type LinkPayload = Omit<CreateMediaAttachmentRequest, 'media_uuid'>;

export function useUploadAndAttachMedia() {
  const { uploadMediaAuto, isUploading, error: uploadError } = useMediaUpload();
  const { mutateAsync: createAttachment, isPending: isLinking, error: linkError } = useCreateMediaAttachmentMutation();
  const { doFetch: doFetchPrimary } = useFetch<ImportPrimaryClientsRequest, ImportPrimaryClientsResponse>();
  const { doFetch: doFetchRelated } = useFetch<ImportRelatedClientsRequest, ImportRelatedClientsResponse>();
  const { doFetch: doFetchLoyalty } = useFetch<ImportLoyaltyProgramsRequest, ImportLoyaltyProgramsResponse>();

  const importPrimaryClients = useMutation({
    mutationFn: (body: ImportPrimaryClientsRequest) =>
      doFetchPrimary('/api/clients/import/primary', {
        method: 'POST',
        body,
      }),
  });

  const importRelatedClients = useMutation({
    mutationFn: (body: ImportRelatedClientsRequest) =>
      doFetchRelated('/api/clients/import/related', {
        method: 'POST',
        body,
      }),
  });

  const importLoyaltyPrograms = useMutation({
    mutationFn: (body: ImportLoyaltyProgramsRequest) =>
      doFetchLoyalty('/api/clients/import/loyalty-numbers', {
        method: 'POST',
        body,
      }),
  });

  const uploadAndAttach = useCallback(
    async (file: File, link: LinkPayload, uploadOptions: UploadOptions) => {
      const uploadRes = await uploadMediaAuto(file, uploadOptions);
      const mediaUuid = firstMediaUuid(uploadRes);
      return createAttachment({
        media_uuid: mediaUuid,
        ...link,
      });
    },
    [createAttachment, uploadMediaAuto]
  );

  const uploadImportedClientsPrimaryPassengerFile = useCallback(
    async (file: File, uploadOptions?: UploadOptions, metadata?: ImportPrimaryClientsRequest['metadata']) => {
      const uploadRes = await uploadMediaAuto(file, {
        ...IMPORTED_CLIENTS_PRIMARY_PASSENGER_UPLOAD_DEFAULTS,
        ...uploadOptions,
      });
      const mediaUuid = firstMediaUuid(uploadRes);
      return importPrimaryClients.mutateAsync({ media_uuid: mediaUuid, metadata });
    },
    [importPrimaryClients, uploadMediaAuto]
  );

  const uploadImportedClientsRelatedFile = useCallback(
    async (file: File, uploadOptions?: UploadOptions, metadata?: ImportRelatedClientsRequest['metadata']) => {
      const uploadRes = await uploadMediaAuto(file, {
        ...IMPORTED_CLIENTS_PRIMARY_PASSENGER_UPLOAD_DEFAULTS,
        ...uploadOptions,
      });
      const mediaUuid = firstMediaUuid(uploadRes);
      return importRelatedClients.mutateAsync({ media_uuid: mediaUuid, metadata });
    },
    [importRelatedClients, uploadMediaAuto]
  );

  const uploadImportedClientsLoyaltyProgramsFile = useCallback(
    async (file: File, uploadOptions?: UploadOptions, metadata?: ImportLoyaltyProgramsRequest['metadata']) => {
      const uploadRes = await uploadMediaAuto(file, {
        ...IMPORTED_CLIENTS_PRIMARY_PASSENGER_UPLOAD_DEFAULTS,
        ...uploadOptions,
      });
      const mediaUuid = firstMediaUuid(uploadRes);
      return importLoyaltyPrograms.mutateAsync({ media_uuid: mediaUuid, metadata });
    },
    [importLoyaltyPrograms, uploadMediaAuto]
  );

  return {
    uploadAndAttach,
    uploadImportedClientsPrimaryPassengerFile,
    uploadImportedClientsRelatedFile,
    uploadImportedClientsLoyaltyProgramsFile,
    isBusy:
      isUploading ||
      isLinking ||
      importPrimaryClients.isPending ||
      importRelatedClients.isPending ||
      importLoyaltyPrograms.isPending,
    uploadError,
    linkError,
  };
}
